import React, { useState } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { Show } from "./content";
import { FieldSpec, useImageSrc, useSite } from "./content/context";
import { AddTile, EditableText, ItemControls } from "./edit/primitives";
import { compact, removeItem, replaceItem } from "./edit/list";
import { parseLocalDate, safeHttpUrl } from "./urls";

interface DatedShow extends Show {
  when: Date;
  /** Position in the stored list, so edits land on the right show. */
  index: number;
}

const formatDate = (date: Date) =>
  date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

/**
 * Shows are one list in the admin page. A show stays "upcoming" through the
 * day it happens, then moves to "past" automatically.
 */
function splitShows(all: Show[], now: Date) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dated: DatedShow[] = all.flatMap((show, index) => {
    const when = parseLocalDate(show.date);
    return when ? [{ ...show, when, index }] : [];
  });
  return {
    upcoming: dated.filter((s) => s.when >= today).sort((a, b) => +a.when - +b.when),
    past: dated.filter((s) => s.when < today).sort((a, b) => +b.when - +a.when),
  };
}

const showFields: FieldSpec[] = [
  { name: "date", label: "Date", type: "date", required: true },
  { name: "venue", label: "Venue", required: true },
  { name: "location", label: "City", placeholder: "Seattle, WA" },
  {
    name: "ticketUrl",
    label: "Ticket link",
    type: "url",
    hint: "Shown while the show is upcoming.",
    validate: (v) => (!v || safeHttpUrl(v) ? undefined : "Enter a full link starting with https://"),
  },
  { name: "poster", label: "Poster", type: "image" },
];

function useShowEditing() {
  const { data, edit } = useSite();
  const shows = data.shows;
  const save = (next: Show[]) => edit?.update("shows", next);

  const openShow = async (index?: number) => {
    if (!edit) return;
    const current = index === undefined ? undefined : shows[index];
    const result = await edit.openForm({
      title: current ? "Edit show" : "Add a show",
      fields: showFields,
      values: { location: "Seattle, WA", ...current },
      submitLabel: current ? "Save" : "Add show",
      deleteLabel: current ? "Delete show" : undefined,
    });
    if (!result || index === undefined) {
      if (result && "values" in result) save([...shows, compact<Show>(result.values)]);
      return;
    }
    if ("deleted" in result) save(removeItem(shows, index));
    else save(replaceItem(shows, index, compact<Show>(result.values)));
  };

  const setField = (index: number, field: keyof Show, value: string) =>
    save(replaceItem(shows, index, compact<Show>({ ...(shows[index] as unknown as Record<string, string>), [field]: value })));

  return { shows, openShow, setField, remove: (index: number) => save(removeItem(shows, index)) };
}

/** How many past shows to show before "Show all". */
export const PAST_LIMIT = 8;

const ShowCard = ({ show, showTickets, small }: { show: DatedShow; showTickets: boolean; small?: boolean }) => {
  const { edit } = useSite();
  const imageSrc = useImageSrc();
  const { shows, openShow, setField, remove } = useShowEditing();
  const poster = imageSrc(show.poster);
  const tickets = showTickets ? safeHttpUrl(show.ticketUrl) : undefined;
  const date = formatDate(show.when);
  return (
    <Card className={`af-show-card${small ? " af-show-compact" : ""}${edit ? " af-frame" : ""}`}>
      {poster ? <Card.Img variant="top" src={poster} alt={`${show.venue} poster, ${date}`} className={small ? "show-poster-compact" : "show-poster"} loading="lazy" /> : null}
      <ItemControls
        noun="show"
        index={show.index}
        count={shows.length}
        onChangePhoto={(path) => setField(show.index, "poster", path)}
        onEdit={() => openShow(show.index)}
        onRemove={() => remove(show.index)}
      />
      <Card.Body>
        <Card.Title>{date}</Card.Title>
        <Card.Text>
          <EditableText as="strong" label="Venue" value={show.venue} onChange={(v) => setField(show.index, "venue", v)} validate={(v) => (v ? undefined : "A show needs a venue.")} />
          {show.location || edit ? (
            <>
              <br />
              <EditableText label="City" value={show.location} onChange={(v) => setField(show.index, "location", v)} />
            </>
          ) : null}
          {tickets ? (
            <>
              <br />
              <a href={tickets} target="_blank" rel="noopener noreferrer">Tickets</a>
            </>
          ) : null}
        </Card.Text>
      </Card.Body>
    </Card>
  );
};

const Shows: React.FC = () => {
  const { edit } = useSite();
  const { shows, openShow } = useShowEditing();
  const { upcoming, past } = splitShows(shows, new Date());
  const [showAllPast, setShowAllPast] = useState(false);
  const pastShown = edit || showAllPast ? past : past.slice(0, PAST_LIMIT);

  return (
    <Container className="my-5">
      {/* Upcoming Shows */}
      <h2 className="af-section-title">Upcoming Shows</h2>
      <Row className="mb-5">
        {upcoming.map((show) => (
          <Col key={show.index} xs={12} md={6} className="mb-4">
            <ShowCard show={show} showTickets />
          </Col>
        ))}
        {upcoming.length === 0 && !edit ? (
          <Col>
            <p className="af-empty">No upcoming shows announced yet. Check back soon.</p>
          </Col>
        ) : null}
        {edit ? (
          <Col xs={12} md={6} className="mb-4">
            <AddTile label="Add a show" onClick={() => openShow()} />
          </Col>
        ) : null}
      </Row>

      {/* Past Shows */}
      <h2 className="af-section-title">Past Shows</h2>
      <Row id="af-past-shows">
        {pastShown.length > 0 ? (
          pastShown.map((show) => (
            <Col key={show.index} xs={6} md={4} lg={3} className="mb-4">
              <ShowCard show={show} showTickets={false} small />
            </Col>
          ))
        ) : (
          <Col>
            <p className="af-empty">No previous shows to display.</p>
          </Col>
        )}
      </Row>
      {!edit && past.length > PAST_LIMIT ? (
        <div className="text-center">
          <button type="button" className="btn af-button-outline" aria-expanded={showAllPast} aria-controls="af-past-shows" onClick={() => setShowAllPast((v) => !v)}>
            {showAllPast ? "Show fewer" : `Show all ${past.length} past shows`}
          </button>
        </div>
      ) : null}
    </Container>
  );
};

export { splitShows };
export default Shows;
